/**
 * The Audio Studio page behind the launch shell's access rule (cowork/clientFeatures.ts studioPageState: Beatly staff,
 * or a client team with `audio` open and not paused): a spinner while /v1/me loads, the studio when open, and
 * otherwise the same calm page every studio shows (closed, paused, or try again when /v1/me never answered).
 * The app shell draws that page first; this keeps Audio Studio shut wherever else it is mounted.
 */
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { StudioUnavailable } from '@/cowork/launch/StudioUnavailable';
import { useCoworkMe } from '@/cowork/launch/useCoworkMe';
import { useStudioPageState } from '@/cowork/launch/useStudioViewer';
import { AudioStudioPage } from './AudioStudioPage';

export function AudioStudioGate({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { user } = useAuth();
  const state = useStudioPageState('audio-studio');
  const me = useCoworkMe();
  const onHome = onNavigate ? () => onNavigate('dashboard') : undefined;
  if (!user) return <StudioUnavailable state="closed" studioName="Audio Studio" onHome={onHome} />;
  if (state === 'loading') {
    return (
      <div role="status" aria-label="Loading Audio Studio" className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#98a2b3]" aria-hidden />
      </div>
    );
  }
  if (state === 'open') return <AudioStudioPage />;
  if (state === 'unreachable') {
    return <StudioUnavailable state="unreachable" studioName="Audio Studio" onRetry={() => { void me.refetch(); }} onHome={onHome} />;
  }
  return <StudioUnavailable state={state === 'paused' ? 'paused' : 'closed'} studioName="Audio Studio" onHome={onHome} />;
}
